import { Modal, Text } from "@wayflyer/flyui";
import { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useIntl } from "react-intl";

const IDLE_TIMEOUT = 1_000 * 60 * 15;
const PROMPT_BEFORE_IDLE = 1_000 * 60 * 5;

const TimeoutText = ({ secondsRemaining }: { secondsRemaining: number }) => {
  const intl = useIntl();

  const wholeSeconds = Math.floor(secondsRemaining);

  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;

  return (
    <Text>
      {intl.formatMessage(
        {
          id: "shared-ui: idleness tracker modal message",
          defaultMessage:
            "You've been idle for a while. For security purposes we'll require you to re-authenticate in {minutes, plural, =1 {# minute} other {# minutes}} and {seconds, plural, =1 {# second} other {# seconds}}.",
        },
        { minutes, seconds }
      )}
    </Text>
  );
};

export const IdlenessTracker = ({ children, onIdle }: { children: React.ReactNode; onIdle: () => void }) => {
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onPrompt: () => {
      setRemainingTime(getRemainingTime() / 1000);
      setShowModal(true);
    },
    onActive: () => {
      setShowModal(false);
    },
    timeout: IDLE_TIMEOUT,
    promptBeforeIdle: PROMPT_BEFORE_IDLE,
    throttle: 1_000,
  });

  useEffect(() => {
    if (!showModal) {
      return;
    }
    const interval = setInterval(() => {
      setRemainingTime(getRemainingTime() / 1000);
    }, 500);
    return () => clearInterval(interval);
  }, [getRemainingTime, showModal]);

  return (
    <>
      <Modal
        title={intl.formatMessage({
          id: "shared-ui: mfa idle title",
          defaultMessage: "Are you still there?",
        })}
        primaryAction={{
          content: intl.formatMessage({
            id: "shared-ui: mfa idle continue",
            defaultMessage: "Continue session",
          }),
          onAction: () => {
            activate();
          },
        }}
        isOpen={showModal}
        onClose={activate}
      >
        <TimeoutText secondsRemaining={remainingTime} />
      </Modal>
      {children}
    </>
  );
};
